import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { useSearchParams } from "react-router-dom";

import { api, type ConsentText } from "../api/client";
import { safeReturnPath } from "../api/oauth";

type Choices = Record<string, boolean>;

function ConsentForm({ consent }: { consent: ConsentText }) {
  const [choices, setChoices] = useState<Choices>({});
  const [params] = useSearchParams();
  const nextPath = safeReturnPath(params.get("next"));
  const start = useMutation({
    mutationFn: () =>
      api.startSignIn({
        base: Boolean(choices.base),
        telemetry: Boolean(choices.telemetry),
        keep_for_future: Boolean(choices.keep_for_future),
        ...(nextPath ? { next_path: nextPath } : {}),
      }),
    onSuccess: ({ redirect_url }) => window.location.assign(redirect_url),
  });
  const ready = consent.layers.every((layer) => !layer.required || choices[layer.id]);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) start.mutate();
      }}
    >
      {consent.layers.map((layer) => (
        <fieldset key={layer.id} className="permission-block">
          <legend>
            {layer.title} {layer.required ? <span className="muted">(required)</span> : null}
          </legend>
          <p>{layer.body}</p>
          <label className="checkbox">
            <input
              type="checkbox"
              name={layer.id}
              checked={Boolean(choices[layer.id])}
              onChange={(e) => setChoices({ ...choices, [layer.id]: e.target.checked })}
            />
            I agree
          </label>
        </fieldset>
      ))}
      <p className="muted">
        Questions or deletion requests:{" "}
        {consent.grievance_contact_email ? (
          <a href={`mailto:${consent.grievance_contact_email}`}>
            {consent.grievance_contact_email}
          </a>
        ) : (
          "contact Tekdi HR"
        )}
        . Consent version {consent.version}.
      </p>
      {start.isError && <p role="alert">{start.error.message}</p>}
      <button type="submit" disabled={!ready || start.isPending}>
        Continue with GitHub
      </button>
    </form>
  );
}

export function ConsentPage() {
  const consent = useQuery({ queryKey: ["consent"], queryFn: api.consent });
  return (
    <main className="page">
      <h1>Before you sign in</h1>
      <p>
        We ask for your consent first, so nothing about you is stored until you agree. You'll then
        sign in with GitHub.
      </p>
      {consent.isPending && <p>Loading…</p>}
      {consent.isError && <p role="alert">We couldn't load this page. Please refresh.</p>}
      {consent.isSuccess && <ConsentForm consent={consent.data} />}
    </main>
  );
}
